import { HttpClient } from '@angular/common/http';
import { Router, ActivatedRoute } from '@angular/router';
import { Input, Output, Component, EventEmitter, SimpleChanges } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { SpaContext } from '../../Classes/spacontext';
import { AuthMethodApiModel } from '../../Services/Models/Auth/AuthMethodApiModel';
import type { SignInOnFinishedEvent } from './SignInOnFinishedEvent';

@Component({
	standalone: false,
	template: `
		<div *ngIf="loading" class="loading">
			<span class="loading-nhs"
				><div>
					<svg width="68" height="27" viewBox="0 0 68 27" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path
							d="M67.05 27V0H0V27H67.05ZM27 2.61L22.33 24.34H15.07L10.5 9.3H10.44L7.44 24.3H1.89L6.56 2.61H13.84L18.32 17.68H18.38L21.45 2.61H27ZM47.77 2.61L43.2 24.34H37.35L39.29 15H32.37L30.43 24.31H24.58L29.12 2.61H35L33.28 10.92H40.19L41.91 2.61H47.77ZM64.58 3.2L63.13 7.53C61.6195 6.83683 59.9716 6.49494 58.31 6.53C56 6.53 54.12 6.87 54.12 8.62C54.12 11.7 62.6 10.55 62.6 17.15C62.6 23.15 57 24.71 51.93 24.71C49.6354 24.6541 47.36 24.2771 45.17 23.59L46.55 19.17C48.2133 20.0225 50.0612 20.4519 51.93 20.42C53.74 20.42 56.59 20.07 56.59 17.83C56.59 14.34 48.11 15.65 48.11 9.52C48.11 3.91 53.05 2.23 57.84 2.23C60.1166 2.13409 62.3906 2.47349 64.54 3.23L64.58 3.2Z"
							fill="#005EB8"
						/>
					</svg>
				</div>
				<div>{{ 'LicenceHolder.Component.SignIn.Nhs.NhsLogin' | translate }}</div>
			</span>
			<p>{{ 'LicenceHolder.Component.SignIn.Nhs.Loading' | translate }}</p>
			<span (click)="resetLoadingState()" class="btn danger">{{ 'Common.Button.Abort' | translate }}</span>
		</div>
		<button class="btn-nhs" *ngIf="!loading && method.MethodId === 'NhsOidc'" (click)="openAuthenticationWindow()">
			<div>
				<svg width="65" height="27" viewBox="0 0 65 27" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path
						d="M64.7122 26.3909V0.450562H0.293579V26.3909H64.7122ZM26.234 2.95813L21.7472 23.8353H14.7722L10.3815 9.42401H10.3239L7.44159 23.8353H2.1094L6.59613 2.95813H13.5904L17.8946 17.4367H17.9522L20.9018 2.95813H26.234ZM46.1888 2.95813L41.7982 23.8353H36.1778L38.0416 14.8619H31.3932L29.5293 23.8065H23.9089L28.2708 2.92931H33.92L32.2675 10.9132H38.9063L40.5588 2.92931L46.1888 2.95813ZM62.3391 3.52498L60.9844 7.68504C59.5332 7.01908 57.95 6.6906 56.3536 6.72429C54.1343 6.72429 52.3281 7.05095 52.3281 8.73227C52.3281 11.6914 60.4753 10.5865 60.4753 16.9275C60.4753 22.692 55.095 24.1908 50.224 24.1908C48.0195 24.1371 45.8333 23.7749 43.7293 23.1148L45.0551 18.8682C46.6532 19.6873 48.4285 20.0998 50.224 20.0692C51.963 20.0692 54.7011 19.7329 54.7011 17.5808C54.7011 14.2278 46.5539 15.4864 46.5539 9.59695C46.5539 4.20711 51.3 2.59304 55.9021 2.59304C58.0893 2.5009 60.2741 2.82698 62.3391 3.5538V3.52498Z"
						fill="white"
					/>
				</svg>
			</div>
			<div>{{ 'LicenceHolder.Component.SignIn.Nhs.Button' | translate }}</div>
		</button>

		<button class="btn" *ngIf="method.MethodId !== 'NhsOidc'" (click)="openAuthenticationWindow()">
			{{ 'Common.Button.SignIn' | translate }}
		</button>
	`,
	selector: 'visibacare-iframe-auth',
})
export class IframeSignInComponent {
	@Input() returnUrl: string;
	@Input('ticket') callTicket: Guid | null;
	@Input() countryCode: string;
	@Input() method: AuthMethodApiModel = null;
	@Output() onFinished = new EventEmitter<SignInOnFinishedEvent>();
	@Output() nhsLoading = new EventEmitter<boolean>();

	authenticationWindow: Window | null = null;
	public loading: boolean = false;

	constructor(
		private _http: HttpClient,
		private _router: Router,
		private _sanitizer: DomSanitizer,
		private _spaContext: SpaContext,
		private _route: ActivatedRoute,
	) {}

	ngAfterViewInit() {
		window.addEventListener('message', this.iframeMessage, false);
	}

	ngOnDestroy() {
		if (this.authenticationWindow) this.authenticationWindow.close();

		window.removeEventListener('message', this.iframeMessage, false);
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes['method'] && this.authenticationWindow) this.authenticationWindow.close();
	}

	openAuthenticationWindow() {
		if (this.authenticationWindow) this.authenticationWindow.close();
		if (this.method.MethodId === 'NhsOidc') {
			this.nhsLoading.emit(true);
			this.loading = true;
		}
		let url = this.method.Schemes.find((m) => m.Scheme === 'WebView').Url;
		if (this.callTicket) {
			url += `&callticket=${this.callTicket}`;
		}
		this.authenticationWindow = window.open(url, '', `height=640,width=480`);
	}

	// See 'Public_old/src/VisibaCare/wwwroot/Scripts/Auth/VisibaAuthV2.js' for type definition.
	iframeMessage = (
		message: MessageEvent<{
			event: 'Authenticated' | 'Failed' | 'Cancelled';
			transaction: {
				Status: string;
				Device: string;
				Token: string;
				RegistrationTicket: string;
				ErrorMessage: string;
				provider: 'dummy_provider';
				token: string;
				success: boolean;
				type: 'authenticated' | 'failed' | 'cancelled';
				errorMessage: string;
			};
		}>,
	) => {
		this.authenticationWindow.close();
		this.authenticationWindow = null;

		switch (message.data.event) {
			case 'Authenticated':
				if (message.data.transaction.Status === 'RegistrationNeeded') {
					this.onFinished.emit({
						code: 'Register',
						data: { ticket: message.data.transaction.RegistrationTicket },
					});
				} else {
					//TODO: Get Client
					this.onFinished.emit({ code: 'Signed' });
				}
				break;
			case 'Failed':
				this.onFinished.emit({
					code: 'Failed',
					data: {
						errorCode: message.data.event,
						errorMessage: message.data.transaction.errorMessage,
					},
				});
				break;
			case 'Cancelled':
				this.onFinished.emit({ code: 'Cancel' });
				break;
		}
	};

	public resetLoadingState() {
		this.nhsLoading.emit(false);
		this.loading = false;
		this.authenticationWindow.close();
	}
}
